// VideoPage.js
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ref, listAll, getDownloadURL, getMetadata } from 'firebase/storage';
import { auth, storage } from './firebase.js';

function VideoPage({ user, isMobile, isAdmin }) {
  const [videoUrl, setVideoUrl] = useState(null);
  const [videos, setVideos] = useState([]);
  const [selectedVideo, setSelectedVideo] = useState('');
  const [error, setError] = useState(null);
  const [triggerStatus, setTriggerStatus] = useState(null);
  const navigate = useNavigate();

  const fetchVideos = useCallback(async () => {
    try {
      const recordingsRef = ref(storage, 'recordings/');
      const listResult = await listAll(recordingsRef);
      if (listResult.items.length === 0) {
        setError('No videos found in the recordings folder.');
        setVideos([]);
        setVideoUrl(null);
        setSelectedVideo('');
        return;
      }

      // Fetch metadata for each video to get updated time
      const videoItemsWithMetadata = await Promise.all(
        listResult.items.map(async (item) => {
          const metadata = await getMetadata(item);
          return { ref: item, name: item.name, updated: metadata.updated };
        })
      );

      // Sort by updated time (newest first)
      videoItemsWithMetadata.sort((a, b) => new Date(b.updated).getTime() - new Date(a.updated).getTime());

      // Format display names (remove timestamp prefix)
      const videoData = videoItemsWithMetadata.map((item) => ({
        name: item.name,
        displayName: item.name.replace(/^\d+_/, ''),
        updated: item.updated,
      }));
      setVideos(videoData);

      // Set the latest video as default
      const latestVideoRef = videoItemsWithMetadata[0].ref;
      const url = await getDownloadURL(latestVideoRef);
      setVideoUrl(url);
      setSelectedVideo(latestVideoRef.name);
      setError(null);
    } catch (err) {
      console.error('Error fetching videos:', err);
      setError('Failed to load videos');
      setVideos([]);
      setVideoUrl(null);
      setSelectedVideo('');
    }
  }, [storage]);

  useEffect(() => {
    if (!user) {
      navigate('/');
      return;
    }
    fetchVideos();
  }, [user, navigate, fetchVideos]);

  const handleVideoSelect = async (event) => {
    const selectedName = event.target.value;
    if (!selectedName) return;

    try {
      const videoRef = ref(storage, `recordings/${selectedName}`);
      const url = await getDownloadURL(videoRef);
      setVideoUrl(url);
      setSelectedVideo(selectedName);
      setError(null);
    } catch (err) {
      console.error('Error fetching selected video:', err);
      setError('Failed to load selected video');
    }
  };

  const handleTriggerCopy = async () => {
    try {
      setTriggerStatus('Triggering video copy...');
      const idToken = await auth.currentUser.getIdToken();
      const response = await fetch(
        'https://us-central1-nantahala-mountain-resor-596d9.cloudfunctions.net/api/triggerVideoCopy',
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${idToken}`,
            'Content-Type': 'application/json',
          },
        }
      );
      const result = await response.json();
      if (response.ok) {
        setTriggerStatus(result.message);
        // Refresh video list after successful trigger
        await fetchVideos();
      } else {
        setTriggerStatus(`Error: ${result.error}`);
      }
    } catch (err) {
      console.error('Error triggering video copy:', err);
      setTriggerStatus('Failed to trigger video copy');
    }
  };

  return (
    <div className={`video-page ${isMobile ? 'video-page-mobile' : ''}`}>
      <h2 className="video-page-title">Webcam Videos</h2>
      {error && <p className="text-red-500">{error}</p>}
      {triggerStatus && (
        <p className={triggerStatus.includes('Error') || triggerStatus.includes('Failed') ? 'text-red-500' : 'text-green-500'}>
          {triggerStatus}
        </p>
      )}
      {isAdmin && (
        <button
          onClick={handleTriggerCopy}
          className="modal-button modal-button-primary mb-4"
        >
          Trigger Video Copy
        </button>
      )}
      <div className="video-selector mb-4">
        <label htmlFor="video-select" className="mr-2 font-semibold">Select Video:</label>
        <select
          id="video-select"
          value={selectedVideo}
          onChange={handleVideoSelect}
          className="modal-input w-full max-w-xs"
          disabled={videos.length === 0}
        >
          {videos.length === 0 ? (
            <option value="">No videos available</option>
          ) : (
            <>
              <option value="">Select a video</option>
              {videos.map((video) => (
                <option key={video.name} value={video.name}>
                  {video.displayName} ({new Date(video.updated).toLocaleString()})
                </option>
              ))}
            </>
          )}
        </select>
      </div>
      {videoUrl ? (
        <video
          controls
          className={`video-player ${isMobile ? 'video-player-mobile' : ''}`}
          src={videoUrl}
          type="video/mp4"
        >
          Your browser does not support the video tag.
        </video>
      ) : (
        !error && <p>Loading video...</p>
      )}
    </div>
  );
}

export default VideoPage;